// src/components/pages/ProjectCarousel/ProjectCarousel.tsx (Notion対応版)
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTransition } from '../../common/Transition/TransitionContext';
import { useAllProjects } from '../../../hooks/useNotionProjects';
import ProjectDetail from '../ProjectDetail/ProjectDetail';
import './ProjectCarousel.css';

const ProjectCarousel: React.FC = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { isTransitioning } = useTransition();
  const [isProjectTransitioning, setIsProjectTransitioning] = useState(false);
  
  // 全プロジェクトデータを取得
  const { projects, loading } = useAllProjects();

  // プロジェクトIDの配列を作成
  const projectIds = Object.keys(projects);
  const currentIndex = projectIds.indexOf(projectId || '');

  // デバッグ情報
  useEffect(() => {
    console.log('🔍 ProjectCarousel Debug:', {
      projectId,
      currentIndex,
      totalProjects: projectIds.length,
      projectIds: projectIds.slice(0, 3), // 最初の3つのIDを表示
      loading
    });
  }, [projectId, projectIds, currentIndex, loading]);

  // スクロール位置をトップにリセット
  useEffect(() => {
    const scrollContainer = document.querySelector('.carousel-slide');
    if (scrollContainer) {
      scrollContainer.scrollTop = 0;
    }
  }, [projectId]);

  const handleNavigation = useCallback(async (direction: 'prev' | 'next') => {
    if (isTransitioning || isProjectTransitioning || loading || projectIds.length === 0) {
      console.log('Navigation blocked:', { isTransitioning, isProjectTransitioning, loading, projectIdsLength: projectIds.length });
      return;
    }

    const newIndex = direction === 'prev' 
      ? (currentIndex > 0 ? currentIndex - 1 : projectIds.length - 1)
      : (currentIndex < projectIds.length - 1 ? currentIndex + 1 : 0);
    
    const targetRoute = `/project/${projectIds[newIndex]}`;
    
    console.log(`Starting project transition from ${projectId} to ${projectIds[newIndex]}`);
    
    try {
      // 同時にコンテンツとフッタータイトルをフェードアウト
      const contentElement = document.querySelector('.fullscreen-scroll-content');
      const footerTitleElement = document.querySelector('.project-title-footer');
      
      if (contentElement) {
        contentElement.classList.remove('content-visible');
        contentElement.classList.add('content-hidden');
      }
      
      if (footerTitleElement) {
        footerTitleElement.classList.add('title-transitioning');
      }
      
      // フェードアウト完了を待つ
      await new Promise(resolve => setTimeout(resolve, 350));
      
      // ページ遷移
      navigate(targetRoute, { replace: true });
      
      // 少し待ってからフェードイン開始
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // 同時にコンテンツとフッタータイトルをフェードイン
      const newContentElement = document.querySelector('.fullscreen-scroll-content');
      const newFooterTitleElement = document.querySelector('.project-title-footer');
      
      if (newContentElement) {
        newContentElement.classList.remove('content-hidden');
        newContentElement.classList.add('content-fading');
        
        setTimeout(() => {
          newContentElement.classList.remove('content-fading');
          newContentElement.classList.add('content-visible');
        }, 50);
      }
      
      if (newFooterTitleElement) {
        newFooterTitleElement.classList.remove('title-transitioning');
      }
      
      // フェードイン完了を待つ
      await new Promise(resolve => setTimeout(resolve, 600));
      
      setIsProjectTransitioning(false);
      
      console.log('Project transition completed successfully');
    } catch (error) {
      console.error('Project transition failed:', error);
      setIsProjectTransitioning(false);
      navigate(targetRoute, { replace: true });
    }
  }, [isTransitioning, isProjectTransitioning, loading, projectIds, currentIndex, projectId, navigate]);

  // キーボードナビゲーション
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handleNavigation('prev');
      } else if (e.key === 'ArrowRight') {
        handleNavigation('next');
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentIndex, isTransitioning, isProjectTransitioning, loading, projectIds.length, handleNavigation]);

  return (
    <div className="project-carousel" data-page="project">
      <div className="carousel-container">
        <div className="carousel-slide active">
          <ProjectDetail projectId={projectId || ''} />
        </div>
      </div>

      {/* カルーセル制御を親コンポーネントに公開（非表示） */}
      <div style={{ display: 'none' }}>
        <button id="carousel-prev" onClick={() => handleNavigation('prev')} />
        <button id="carousel-next" onClick={() => handleNavigation('next')} />
      </div>
    </div>
  );
};

export default ProjectCarousel;